import { OrderEventOriginType } from "../dto/order-event-origin-type.enum";
import { OrderEventStatus } from "../dto/order-event-status.enum";
import { OrderEventType } from "../dto/order-event-type.model";

	/**
	 * @description
	 *  Modelo de una nota de CyR (cambio y reclamo), o NC (nota de crédito),
	 *	sobre un pedido, para búsquedas paginadas.
	 *
	 * @see gigot-backend/Services/OrderEventService.cs
	 */

export class OrderEventSearch {

	public active: boolean;
	public amount: number;
	public campaignNumber: string;
	public creationDate: Date;
	public firstName: string;
	public generateCreditNote: boolean;
	public id: number;
	public lastName: string;
	public orderEventOriginType: OrderEventOriginType;
	public orderEventStatus: OrderEventStatus;
	public orderEventType: OrderEventType;
	public orderEventTypeId: number;
	public timestamp: Date;
	public userName: string;
	public zone: number;

	/**
	 * @description
	 *	Construye una nota con una semántica vacía. Nótese que no es lo mismo
	 *	que construir una nota con valores por defecto. Las reglas generales
	 *	de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Una nota de CyR/NC vacía.
	 */
	public static empty(): OrderEventSearch {
		const orderEventSearch: OrderEventSearch = new OrderEventSearch();
		orderEventSearch.active = false;
		orderEventSearch.amount = undefined;
		orderEventSearch.campaignNumber = "";
		orderEventSearch.creationDate = undefined;
		orderEventSearch.firstName = "";
		orderEventSearch.generateCreditNote = false;
		orderEventSearch.id = undefined;
		orderEventSearch.lastName = "";
		orderEventSearch.orderEventOriginType = undefined;
		orderEventSearch.orderEventStatus = undefined;
		orderEventSearch.orderEventType = OrderEventType.empty();
		orderEventSearch.orderEventTypeId = undefined;
		orderEventSearch.timestamp = undefined;
		orderEventSearch.userName = "";
		orderEventSearch.zone = undefined;
		return orderEventSearch;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicOrderEventSearch
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicOrderEventSearch: OrderEventSearch): OrderEventSearch {
		if (!anemicOrderEventSearch) return undefined;
		const orderEventSearch: OrderEventSearch = OrderEventSearch.empty();
		orderEventSearch.active = anemicOrderEventSearch.active;
		orderEventSearch.amount = anemicOrderEventSearch.amount;
		orderEventSearch.campaignNumber = anemicOrderEventSearch.campaignNumber;
		orderEventSearch.creationDate = new Date(anemicOrderEventSearch.creationDate);
		orderEventSearch.firstName = anemicOrderEventSearch.firstName;
		orderEventSearch.generateCreditNote = anemicOrderEventSearch.generateCreditNote;
		orderEventSearch.id = anemicOrderEventSearch.id;
		orderEventSearch.lastName = anemicOrderEventSearch.lastName;
		orderEventSearch.orderEventOriginType = anemicOrderEventSearch.orderEventOriginType;
		orderEventSearch.orderEventStatus = anemicOrderEventSearch.orderEventStatus;
		orderEventSearch.orderEventType = OrderEventType.from(anemicOrderEventSearch.orderEventType);
		orderEventSearch.orderEventTypeId = anemicOrderEventSearch.orderEventTypeId;
		orderEventSearch.timestamp = new Date(anemicOrderEventSearch.timestamp);
		orderEventSearch.userName = anemicOrderEventSearch.userName;
		orderEventSearch.zone = anemicOrderEventSearch.zone;
		return orderEventSearch;
	}
}
